// src/components/AdminVendorList.js
import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  Row, Col, Form, Button, Spinner, Alert, Offcanvas
} from "react-bootstrap";
import "./AdminVendorList.css";

/* ===================== small utils ===================== */
const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
const orDash = (v) =>
  v === 0 ? "0" : (v == null || String(v).trim() === "" ? "—" : String(v));
const tel = (x) => (x ? <a href={`tel:${x}`}>{x}</a> : "—");
const mail = (x) => (x ? <a href={`mailto:${x}`}>{x}</a> : "—");
const toCSV = (rows) =>
  "\ufeff" + rows.map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
const getInitials = (name = "") =>
  String(name || "")
    .split(" ")
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "VM";
const getAvatarTone = (seed = "") => {
  const tones = ["blue", "green", "amber", "violet", "rose", "sky"];
  const total = String(seed || "").split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return tones[total % tones.length];
};

/* ===================== robust extractors ===================== */
const isEmptyish = (v) => {
  if (v == null) return true;
  const s = String(v).trim().toLowerCase();
  return !s || s === "na" || s === "n/a" || s === "null" || s === "undefined";
};

const getPath = (obj, path) =>
  path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const pickFirstDeep = (obj, paths = []) => {
  for (const p of paths) {
    const val = getPath(obj, p);
    if (!isEmptyish(val)) return val;
  }
  return "";
};

const extractGST = (raw) => {
  const v =
    pickFirstDeep(raw, [
      "gstNumber","gst","gstin","gst_no","GSTNumber","GST",
      "vendor.gstNumber","vendor.gst","vendor.gstin","vendor.gst_no","vendor.GSTNumber","vendor.GST",
      "profile.gstNumber","profile.gst","profile.gstin",
      "userId.gstNumber","userId.gst","userId.gstin","userId.gst_no"
    ]);
  return v ? String(v).trim() : "";
};

const extractCompany = (raw) => {
  const v = pickFirstDeep(raw, [
    "profile.companyName","vendor.companyName","companyName","fullName","vendor.fullName","profile.fullName"
  ]);
  return v ? String(v).trim() : "";
};

const extractVendorType = (raw) =>
  String(pickFirstDeep(raw, ["vendorType","type","vendor.vendorType","vendor.type","profile.vendorType"]) || "");

const extractTurnover = (raw) =>
  String(pickFirstDeep(raw, ["turnover","vendor.turnover","profile.turnover"]) || "");

const extractProductInterest = (raw) => {
  let v = pickFirstDeep(raw, ["productInterest","vendor.productInterest","profile.productInterest"]);
  if (Array.isArray(v)) v = v.join(", ");
  return String(v || "");
};

const makeAddressText = (obj) => {
  if (!obj) return "";
  if (typeof obj === "string") {
    const s = obj.trim();
    return s && s !== "N/A" && s !== "NA" ? s : "";
  }
  const {
    address, line1, line2, area, city, district, state, pincode,
    lat, lng, latitude, longitude, lines
  } = obj || {};
  const parts = [
    address, line1, line2, area, (city || district), state, pincode
  ].map(x => (x || "").toString().trim()).filter(Boolean);
  if (parts.length) return parts.join(", ");
  if (Array.isArray(lines) && lines.filter(Boolean).length) return lines.filter(Boolean).join(", ");
  const la = lat ?? latitude, lo = lng ?? longitude;
  if (la != null && lo != null) return `${la}, ${lo}`;
  return "";
};

const extractAddressText = (raw) => {
  const ready = pickFirstDeep(raw, ["addressText","vendor.addressText","profile.addressText"]);
  if (!isEmptyish(ready)) return String(ready);
  const direct = pickFirstDeep(raw, ["address","vendor.address","profile.address"]);
  if (direct && typeof direct === "string") return direct;
  const addrObj = getPath(raw, "address") || getPath(raw, "vendor.address") || getPath(raw, "profile.address");
  if (addrObj && typeof addrObj === "object") return makeAddressText(addrObj);

  const geo = getPath(raw, "geo") || getPath(raw, "vendor.geoLocation") ||
              getPath(raw, "vendor.location") || getPath(raw, "profile.geoLocation") ||
              getPath(raw, "geoLocation") || getPath(raw, "location");
  const la = Number(geo?.lat ?? geo?.latitude);
  const lo = Number(geo?.lng ?? geo?.longitude);
  if (Number.isFinite(la) && Number.isFinite(lo)) return `${la}, ${lo}`;

  return "";
};

const extractGeo = (raw) => {
  const geo = getPath(raw, "geo") || getPath(raw, "vendor.geoLocation") || getPath(raw, "vendor.location") ||
              getPath(raw, "profile.geoLocation") || getPath(raw, "geoLocation") || getPath(raw, "location");
  const lat = Number(geo?.lat ?? geo?.latitude);
  const lng = Number(geo?.lng ?? geo?.longitude);
  return (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : null;
};

/* Normalize for table row */
const toViewModel = (raw) => {
  if (!raw) return null;
  const src = raw.profile || raw.vendor || raw;

  const fullName = String(pickFirstDeep(raw, ["fullName","vendor.fullName","profile.fullName"]) || "");
  const email    = String(pickFirstDeep(raw, ["email","vendor.email","profile.email"]) || "");
  const mobile   = String(pickFirstDeep(raw, ["mobile","vendor.mobile","profile.mobile"]) || "");

  const companyName     = extractCompany(raw) || src.companyName || "";
  const vendorType      = extractVendorType(raw) || src.vendorType || "";
  const gstNumber       = extractGST(raw) || src.gstNumber || "";
  const addressText     = extractAddressText(raw) || src.addressText || "";
  const turnover        = extractTurnover(raw) || src.turnover || "";
  const productInterest = extractProductInterest(raw) || src.productInterest || "";
  const geo             = extractGeo(raw) || src.geoLocation || null;

  const approved = (typeof raw.approved === "boolean")
    ? raw.approved
    : (typeof src.approved === "boolean" ? src.approved : false);

  const certificateStatus =
    pickFirstDeep(raw, ["certificateStatus","vendor.certificateStatus","profile.certificateStatus"]) ||
    (src.certificateIssued ? "generated" : "pending");

  const createdAt = raw.createdAt || src.createdAt || null;
  const updatedAt = raw.updatedAt || src.updatedAt || null;

  return {
    _id: String(raw._id || src._id || ""),
    fullName,
    email,
    mobile,
    companyName,
    vendorType,
    gstNumber,
    addressText,
    approved,
    certificateStatus,
    turnover,
    productInterest,
    createdAt,
    updatedAt,
    geo,
  };
};

/* ===================== UI bits ===================== */
const StatusPills = ({ approved, certificateStatus, hasGeo }) => (
  <div className="vm-status-stack">
    <span className={`vm-status-pill ${approved ? "approved" : "pending"}`}>
      {approved ? "Approved" : "Pending"}
    </span>
    <span className={`vm-mini-pill ${certificateStatus === "generated" ? "verified" : "waiting"}`}>
      Cert: {certificateStatus === "generated" ? "generated" : (certificateStatus || "pending")}
    </span>
    <span className={`vm-mini-pill ${hasGeo ? "neutral" : "waiting"}`}>
      Geo: {hasGeo ? "set" : "na"}
    </span>
  </div>
);

const EmptyState = ({ onClear }) => (
  <div className="text-center text-muted py-5 border rounded">
    <div style={{ fontSize: 48, lineHeight: 1 }}>🗂️</div>
    <div className="mt-2">No vendors found.</div>
    {onClear && <Button className="mt-3" variant="outline-secondary" onClick={onClear}>Clear filters</Button>}
  </div>
);

/* ===================== Main Component ===================== */
const AdminVendorList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);           // raw docs from API (user + profile)
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState(null); // {variant, text}
  const [actionLoading, setActionLoading] = useState(null);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("all"); // all|approved|pending
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" }); // asc|desc
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showFilters, setShowFilters] = useState(false);

  // Debounce search typing
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchVendors = async () => {
    setLoading(true);
    setNote(null);
    try {
      const res = await API.get("/admin/vendors", { params: { full: 1 } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.vendors || []);
      setRows(list);
    } catch (err) {
      console.error("Vendor load failed:", err);
      const code = err?.response?.status;
      setNote({ variant: "danger", text: `Failed to load vendors${code ? ` (${code})` : ""}` });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const confirm = async (fn, okMessage) => {
    try {
      await fn();
      setNote({ variant: "success", text: okMessage });
      await fetchVendors();
    } catch (e) {
      console.error(e);
      setNote({ variant: "danger", text: "Operation failed" });
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    await confirm(() => API.put(`/admin/vendors/${id}/approve`), "Vendor approved");
    setActionLoading(null);
  };

  const handleReject = async (id) => {
    if (!window.confirm("Reject this vendor?")) return;
    setActionLoading(id);
    await confirm(() => API.put(`/admin/vendors/${id}/reject`), "Vendor rejected");
    setActionLoading(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor? This action cannot be undone.")) return;
    setActionLoading(id);
    await confirm(() => API.delete(`/admin/vendors/${id}`), "Vendor deleted");
    setActionLoading(null);
  };

  const handleSendWelcome = async (id) => {
    setActionLoading(id);
    try {
      const res = await API.post(`/admin/whatsapp/send-registration-welcome/${id}`);
      setNote({ variant: "success", text: res.data?.message || "Welcome WhatsApp sent" });
    } catch (e) {
      setNote({ variant: "danger", text: e?.response?.data?.message || "Failed to send welcome WhatsApp" });
    } finally {
      setActionLoading(null);
    }
  };

  // New tab link
  const viewHref = (id) => `/admin/vendors/${id}/account`;

  // Filtering + sorting on VM
  const vms = useMemo(() => rows.map(toViewModel).filter(Boolean), [rows]);

  const filtered = useMemo(() => {
    const result = vms.filter(v => {
      const matchText =
        (v.fullName || "").toLowerCase().includes(debounced) ||
        (v.companyName || "").toLowerCase().includes(debounced) ||
        (v.vendorType || "").toLowerCase().includes(debounced) ||
        (v.gstNumber || "").toLowerCase().includes(debounced) ||
        (v.addressText || "").toLowerCase().includes(debounced) ||
        (v.turnover || "").toLowerCase().includes(debounced) ||
        (v.mobile || "").includes(search);
      const matchStatus =
        status === "all" ? true :
        status === "approved" ? v.approved :
        status === "pending" ? !v.approved : true;
      return matchText && matchStatus;
    });
    // Sorting
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    result.sort((a, b) => {
      const A = (a[key] == null ? "" : a[key]);
      const B = (b[key] == null ? "" : b[key]);
      if (key === "createdAt" || key === "updatedAt") {
        const aT = A ? new Date(A).getTime() : 0;
        const bT = B ? new Date(B).getTime() : 0;
        return (aT - bT) * dir;
      }
      return String(A).localeCompare(String(B)) * dir;
    });
    return result;
  }, [vms, debounced, search, status, sort]);

  // Pagination
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(page, pages);
  const startIdx = (clampedPage - 1) * pageSize;
  const pageRows = filtered.slice(startIdx, startIdx + pageSize);

  useEffect(() => {
    setPage(1); // reset when controls change
  }, [debounced, status, pageSize]);

  const exportCSV = () => {
    const headers = [
      "Name","Mobile","Email","Company","VendorType","GST","Approved",
      "Certificate","Turnover","ProductInterest","Address","CreatedAt","UpdatedAt","Lat","Lng"
    ];
    const rowsCSV = filtered.map(v => [
      v.fullName, v.mobile, v.email, v.companyName, v.vendorType, v.gstNumber,
      v.approved ? "Yes" : "No",
      v.certificateStatus || "",
      orDash(v.turnover), orDash(v.productInterest),
      v.addressText,
      v.createdAt ? fmtIST(v.createdAt) : "",
      v.updatedAt ? fmtIST(v.updatedAt) : "",
      v.geo?.lat ?? "", v.geo?.lng ?? ""
    ]);
    const blob = new Blob([toCSV([headers, ...rowsCSV])], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "vendors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => { setSearch(""); setStatus("all"); };
  const statusCounts = useMemo(
    () => ({
      all: vms.length,
      approved: vms.filter((v) => v.approved).length,
      pending: vms.filter((v) => !v.approved).length,
    }),
    [vms]
  );

  /* ===================== Render ===================== */
  return (
    <div className="vm-page">
      <div className="vm-container">
        <div className="vm-topbar">
          <div className="vm-title-wrap">
            <div className="vm-logo">VM</div>
            <div>
              <div className="vm-title">Vendor Management</div>
              <div className="vm-subtitle vm-subtitle-normal">Manage and monitor all vendors</div>
            </div>
          </div>
          <div className="vm-search vm-search-top d-none d-md-block">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search vendors by name, company, GST, product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="vm-actions">
            <button className="vm-toolbar-btn vm-toolbar-btn-primary" onClick={exportCSV}>
              <span className="material-symbols-outlined">download</span>
              <span>Export CSV</span>
            </button>
            <button className="vm-toolbar-btn" onClick={fetchVendors}>
              <span className="material-symbols-outlined">refresh</span>
              <span>Refresh</span>
            </button>
            <Button className="d-md-none vm-filters-btn" variant="secondary" size="sm" onClick={() => setShowFilters(true)}>
              Filters
            </Button>
            <button className="vm-toolbar-btn vm-toolbar-btn-primary d-none d-md-inline-flex" onClick={() => setShowFilters(true)}>
              <span className="material-symbols-outlined">filter_list</span>
              <span>Filters</span>
            </button>
          </div>
        </div>

        <div className="vm-toolbar">
          <div>
            <div className="vm-tabs">
              <button className={`vm-tab ${status === "all" ? "active" : ""}`} onClick={() => setStatus("all")}>
                <span>All Vendors</span>
                <span className="vm-tab-badge blue">{statusCounts.all}</span>
              </button>
              <button className={`vm-tab ${status === "approved" ? "active" : ""}`} onClick={() => setStatus("approved")}>
                <span>Approved</span>
                <span className="vm-tab-badge green">{statusCounts.approved}</span>
              </button>
              <button className={`vm-tab ${status === "pending" ? "active" : ""}`} onClick={() => setStatus("pending")}>
                <span>Pending</span>
                <span className="vm-tab-badge amber">{statusCounts.pending}</span>
              </button>
            </div>
          </div>

          <div className="vm-filter-row">
            <div className="vm-filter-stack">
              <div className="d-flex gap-2">
                <div className="vm-sort-label">Sort by</div>
                <Form.Select
                  size="sm"
                  value={sort.key}
                  onChange={(e) => setSort((s) => ({ ...s, key: e.target.value }))}
                >
                  <option value="createdAt">Sort: Created</option>
                  <option value="fullName">Sort: Name</option>
                  <option value="companyName">Sort: Company</option>
                  <option value="vendorType">Sort: Type</option>
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={sort.dir}
                  onChange={(e) => setSort((s) => ({ ...s, dir: e.target.value }))}
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </Form.Select>
              </div>
              <div className="vm-search d-md-none">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Filters (Mobile Offcanvas) */}
        <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="bottom">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Filters</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form.Group className="mb-3">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="name, company, GST, address, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sort By</Form.Label>
              <Form.Select value={sort.key} onChange={(e) => setSort((s) => ({ ...s, key: e.target.value }))}>
                <option value="createdAt">Created Date</option>
                <option value="fullName">Name</option>
                <option value="companyName">Company</option>
                <option value="vendorType">Type</option>
              </Form.Select>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button onClick={() => setShowFilters(false)}>Apply</Button>
              <Button variant="secondary" onClick={clearFilters}>Clear</Button>
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        {note && <Alert variant={note.variant} onClose={() => setNote(null)} dismissible>{note.text}</Alert>}

        <div className="vm-content">
          {loading ? (
            <div className="text-center my-5"><Spinner animation="border" /></div>
          ) : filtered.length === 0 ? (
            <EmptyState onClear={clearFilters} />
          ) : (
            <Row xs={1} md={2} xl={3} className="g-3">
              {pageRows.map(v => (
                <Col key={v._id}>
                  <div className="vm-entity-grid-card">
                    <div className="vm-entity-head">
                      <div className="vm-supplier-head">
                        <div className={`vm-avatar vm-avatar-${getAvatarTone(v._id || v.companyName || v.fullName)}`}>
                          {getInitials(v.companyName || v.fullName)}
                        </div>
                        <div className="vm-supplier-copy">
                          <div className="vm-person-name">{orDash(v.companyName || v.fullName)}</div>
                          <div className="vm-company-name">{orDash(v.fullName)}</div>
                          <div className="vm-code-chip">{orDash(v.vendorType || "Vendor")}</div>
                        </div>
                      </div>
                      <StatusPills approved={v.approved} certificateStatus={v.certificateStatus} hasGeo={!!v.geo} />
                    </div>
                    <div className="vm-kv-grid">
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">smartphone</span>Mobile</span>
                        <span className="vm-v">{tel(v.mobile)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">mail</span>Email</span>
                        <span className="vm-v">{mail(v.email)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">badge</span>GST Number</span>
                        <span className="vm-v vm-v-mono">{orDash(v.gstNumber)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">inventory_2</span>Interest</span>
                        <span className="vm-v">{orDash(v.productInterest)}</span>
                      </div>
                      <div className="vm-kv">
                        <span className="vm-k"><span className="material-symbols-outlined">payments</span>Turnover</span>
                        <span className="vm-v">{orDash(v.turnover)}</span>
                      </div>
                      <div className="vm-kv vm-kv-full">
                        <span className="vm-k"><span className="material-symbols-outlined">location_on</span>Address</span>
                        <span className="vm-v">{orDash(v.addressText)}</span>
                      </div>
                    </div>
                    <div className="vm-meta-row">
                      <div className="vm-meta-chip">
                        <span className="material-symbols-outlined">calendar_today</span>
                        <span>Created: {fmtIST(v.createdAt)}</span>
                      </div>
                      <div className="vm-meta-chip">
                        <span className="material-symbols-outlined">update</span>
                        <span>Updated: {fmtIST(v.updatedAt)}</span>
                      </div>
                    </div>
                    <div className="vm-po-grid">
                      <div className="vm-po-cell">
                        <span className="vm-k"><span className="material-symbols-outlined">storefront</span>Vendor Type</span>
                        <span className="vm-v">{orDash(v.vendorType)}</span>
                      </div>
                      <div className="vm-po-cell">
                        <span className="vm-k"><span className="material-symbols-outlined">pin_drop</span>Geo Status</span>
                        <span className="vm-v">{v.geo ? `${v.geo.lat}, ${v.geo.lng}` : "Not set"}</span>
                      </div>
                    </div>
                    <div className="vm-actions-wrap">
                      <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(viewHref(v._id))}>
                        <span className="material-symbols-outlined">badge</span>
                        <span>View Account</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(`/admin/vendors/${v._id}/account?tab=history`)}>
                        <span className="material-symbols-outlined">history</span>
                        <span>View History</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-cyan" onClick={() => navigate(`/admin/payment-history?vendorId=${encodeURIComponent(v._id)}&tab=ledger`)}>
                        <span className="material-symbols-outlined">receipt_long</span>
                        <span>Ledger</span>
                      </Button>
                      <Button size="sm" className="vm-action-btn vm-action-green" disabled={actionLoading === v._id} onClick={() => handleSendWelcome(v._id)}>
                        <span className="material-symbols-outlined">chat</span>
                        <span>{actionLoading === v._id ? "Sending..." : "Send Welcome"}</span>
                      </Button>
                      {v.approved ? (
                        <Button size="sm" className="vm-action-btn vm-action-amber" disabled={actionLoading === v._id} onClick={() => handleReject(v._id)}>
                          <span className="material-symbols-outlined">close</span>
                          <span>{actionLoading === v._id ? "Rejecting..." : "Reject"}</span>
                        </Button>
                      ) : (
                        <Button size="sm" className="vm-action-btn vm-action-success" disabled={actionLoading === v._id} onClick={() => handleApprove(v._id)}>
                          <span className="material-symbols-outlined">check</span>
                          <span>{actionLoading === v._id ? "Approving..." : "Approve"}</span>
                        </Button>
                      )}
                      <Button size="sm" className="vm-action-btn vm-action-danger-fill" disabled={actionLoading === v._id} onClick={() => handleDelete(v._id)}>
                        <span className="material-symbols-outlined">delete</span>
                        <span>{actionLoading === v._id ? "Deleting..." : "Delete"}</span>
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {!loading && total > 0 && (
            <div className="vm-pagination mt-4">
              <div className="text-muted small">
                Showing <strong>{startIdx + 1}</strong> to <strong>{Math.min(total, startIdx + pageSize)}</strong> of <strong>{total}</strong> vendors
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Select
                  size="sm"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{ width: 110 }}
                >
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={clampedPage === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={clampedPage === pages}
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                >
                  Next
                </Button>
                <Button variant="outline-secondary" size="sm" disabled>
                  {clampedPage} / {pages}
                </Button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminVendorList;
